import axios from 'axios';

// Helper to get environment variable from runtime config or build-time env
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  // @ts-ignore - window.env is injected at runtime via env-config.js
  if (typeof window !== 'undefined' && window.env && window.env[key]) {
    // @ts-ignore
    return window.env[key] || defaultValue;
  }
  return (process.env[key] as string) || defaultValue;
};

// Create axios instance following OpenAPI specification
// Use relative path for production (proxied via Ingress)
const API_BASE_URL = getEnvVar('REACT_APP_API_BASE_URL', '');

const api = axios.create({
    baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : '/api/v1',
    timeout: 10000,
});

// Note: Axios interceptors for Keycloak authentication are set up in App.tsx
// using setupAxiosKeycloak() after Keycloak initialization

// Station service following OpenAPI specification
export const stationService = {
    // GET /api/v1/stations/ - List stations
    getAll: async (params?: {
        skip?: number;
        limit?: number;
        include_public?: boolean;
    }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
            if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
            if (params?.include_public !== undefined) queryParams.append('include_public', params.include_public.toString());

            const response = await api.get(`/stations/?${queryParams.toString()}`);

            // Handle paginated response from DRF
            if (response.data && typeof response.data === 'object' && Array.isArray(response.data.results)) {
                return response.data.results; // Return just the results array
            }

            // Handle direct array response (backward compatibility)
            if (Array.isArray(response.data)) {
                return response.data;
            }

            // If neither, return empty array
            return [];
        } catch (error) {
            console.error('Failed to fetch stations from API:', error);
            throw error;
        }
    },

    // GET /api/v1/stations/{station_uuid} - Get station
    getById: async (stationUuid: string) => {
        const response = await api.get(`/stations/${stationUuid}/`);
        return response.data;
    },

    // POST /api/v1/stations/ - Create station
    create: async (data: {
        name: string;
        description?: string;
        location: {
            latitude: number;
            longitude: number;
        };
        public?: boolean;
        parser_config?: object;
    }) => {
        const response = await api.post('/stations/', data);
        return response.data;
    },

    // PUT /api/v1/stations/{station_uuid} - Update station
    update: async (stationUuid: string, data: {
        name?: string;
        description?: string;
        location?: {
            latitude: number;
            longitude: number;
        };
        public?: boolean;
        status?: string;
        parser_config?: object;
    }) => {
        const response = await api.put(`/stations/${stationUuid}/`, data);
        return response.data;
    },

    // DELETE /api/v1/stations/{station_uuid} - Delete station
    delete: async (stationUuid: string) => {
        await api.delete(`/stations/${stationUuid}/`);
    },

    // POST /api/v1/stations/{station_id}/regenerate-api-key - Regenerate API key
    regenerateApiKey: async (stationId: string) => {
        const response = await api.post(`/stations/${stationId}/regenerate-api-key/`);
        return response.data;
    },

    // GET /api/v1/stations/{station_uuid}/timeseries - Get station timeseries
    getTimeseries: async (stationUuid: string, hours: number = 24, limit?: number) => {
        let url = `/stations/${stationUuid}/timeseries/?hours=${hours}`;
        if (limit !== undefined) {
            url += `&limit=${limit}`;
        }
        const response = await api.get(url);
        return response.data;
    },
};

// Measurements service following OpenAPI specification
export const measurementsService = {
    // GET /api/v1/measurements/station/{station_id}/latest - Get latest measurements
    getLatest: async (stationId: number, limit: number = 1) => {
        const response = await api.get(`/measurements/station/${stationId}/latest/?limit=${limit}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/range - Get measurements range
    getRange: async (stationId: number, params?: {
        start_time?: string;
        end_time?: string;
        hours_back?: number;
        measures?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params?.start_time) queryParams.append('start_time', params.start_time);
        if (params?.end_time) queryParams.append('end_time', params.end_time);
        if (params?.hours_back) queryParams.append('hours_back', params.hours_back.toString());
        if (params?.measures) queryParams.append('measures', params.measures);

        const response = await api.get(`/measurements/station/${stationId}/range/?${queryParams.toString()}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/aggregated - Get aggregated measurements
    getAggregated: async (stationId: number, params?: {
        interval?: string;
        hours_back?: number;
        start_time?: string;
        end_time?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params?.interval) queryParams.append('interval', params.interval);
        if (params?.hours_back) queryParams.append('hours_back', params.hours_back.toString());
        if (params?.start_time) queryParams.append('start_time', params.start_time);
        if (params?.end_time) queryParams.append('end_time', params.end_time);

        const response = await api.get(`/measurements/station/${stationId}/aggregated/?${queryParams.toString()}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/measures - Get available measures
    getMeasures: async (stationId: number) => {
        const response = await api.get(`/measurements/station/${stationId}/measures/`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/summary - Get summary stats
    getSummary: async (stationId: number, days: number = 7) => {
        const response = await api.get(`/measurements/station/${stationId}/summary/?days=${days}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/hybrid - Get hybrid data
    getHybrid: async (stationId: number) => {
        const response = await api.get(`/measurements/station/${stationId}/hybrid/`);
        return response.data;
    },
};

// Data Ingestion service following OpenAPI specification
export const ingestionService = {
    // POST /api/v1/ingest/{station_api_key} - Ingest HTTP data
    ingestData: async (stationApiKey: string, payload: object, timestamp?: string) => {
        const headers: Record<string, string> = {};
        if (timestamp) {
            headers['X-Timestamp'] = timestamp;
        }

        const response = await api.post(`/ingest/${stationApiKey}/`, payload, { headers });
        return response.data;
    },

    // GET /api/v1/ingest/{station_api_key}/info - Get ingestion info
    getInfo: async (stationApiKey: string) => {
        const response = await api.get(`/ingest/${stationApiKey}/info/`);
        return response.data;
    },

    // POST /api/v1/ingest/sensor/{station_uuid} - Ingest sensor data by UUID
    ingestByUuid: async (stationUuid: string, payload: object, timestamp?: string) => {
        const headers: Record<string, string> = {};
        if (timestamp) {
            headers['X-Timestamp'] = timestamp;
        }

        const response = await api.post(`/ingest/sensor/${stationUuid}/`, payload, { headers });
        return response.data;
    },
};

// Authentication service following OpenAPI specification
export const authService = {
    // POST /api/v1/auth/login - Login for access token
    login: async (credentials: { username: string; password: string }) => {
        // Use form data format for OAuth2 compatibility as per OpenAPI spec
        const formData = new URLSearchParams();
        formData.append('username', credentials.username);
        formData.append('password', credentials.password);

        const response = await api.post('/auth/login/', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        return response.data;
    },

    // POST /api/v1/auth/register - Register new user
    register: async (userData: {
        email: string;
        password: string;
        first_name?: string;
        last_name?: string;
    }) => {
        const response = await api.post('/auth/register/', userData);
        return response.data;
    },
};

// Dashboard service following OpenAPI specification
export const dashboardService = {
    // GET /api/v1/dashboard/stats - Get dashboard stats
    getStats: async () => {
        const response = await api.get('/dashboard/stats/');
        return response.data;
    },
};

// Map service following OpenAPI specification
export const mapService = {
    // GET /api/v1/map/stations - Get map stations
    getStations: async () => {
        const response = await api.get('/map/stations/');
        return response.data;
    },

    // GET /api/v1/map/stations/bounds - Get stations bounds
    getBounds: async () => {
        const response = await api.get('/map/stations/bounds/');
        return response.data;
    },

    // GET /api/v1/map/stations/{station_id}/status - Get station status
    getStationStatus: async (stationId: string) => {
        const response = await api.get(`/map/stations/${stationId}/status/`);
        return response.data;
    },
};

// Health check service
export const healthService = {
    // GET /health - Health check
    check: async () => {
        const response = await api.get('/health', { baseURL: API_BASE_URL });
        return response.data;
    },

    // GET / - Root endpoint
    root: async () => {
        const response = await api.get('/', { baseURL: API_BASE_URL });
        return response.data;
    },
};

// Legacy compatibility - keeping for backward compatibility with existing code
export const systemHealthService = {
    get: async () => {
        try {
            return await dashboardService.getStats();
        } catch (error) {
            console.error('Failed to fetch system health:', error);
            throw error;
        }
    },
};

// Legacy compatibility - keeping for backward compatibility
export const alertsService = {
    getAll: async () => {
        // TODO: Implement alerts endpoint when available in backend
        return [];
    },
};

// Backward compatibility alias
export const stationTimeseriesService = {
    getTimeseries: stationService.getTimeseries,
};

// Type definitions for better type safety
export interface Station {
    uuid: string;
    name: string;
    description?: string;
    location: {
        latitude: number;
        longitude: number;
    };
    public: boolean;
    status: string;
    api_key?: string;
    mqtt_username?: string;
    created_at: string;
    updated_at?: string;
    last_data_received?: string;
    source?: string;
    owner?: string;
    elevation?: number;
    timezone?: string;
}

export interface StationListItem {
    uuid: string;
    name: string;
    location: {
        latitude: number;
        longitude: number;
    };
    public: boolean;
    status: string;
    latest_readings?: Record<string, {
        value: number;
        unit: string;
        timestamp: string;
    }>;
    source?: string;
    owner?: string;
    elevation?: number;
    timezone?: string;
    debug?: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface Token {
    access_token: string;
    token_type: string;
}

export interface UserRegister {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
}

export default api;
